import React from 'react';
import { Input } from '@/components/ui/input';
import { PriceInput } from './PriceInput';
import { Switch } from '@/components/ui/switch';
import { Section, Field, AddBtn, DelBtn, DeliveryHoursEditor, ItemImagesField, withImages } from './TemplateFields';
import { itemImages } from '../../lib/assets';
import { Plus, CreditCard, Settings2 } from 'lucide-react';

// Shared restaurant "engine" editor blocks. Used by BOTH the Menu (restaurant)
// template editor AND the "special" page editor so the menu/ordering/table
// features are built the same way everywhere — no duplicated behaviour.

export function ItemOptionsEditor({ item, onChange, ci, ii, packagingPerItem }) {
  const groups = item.option_groups || [];
  const setGroups = (g) => onChange({ ...item, option_groups: g });
  const addons = item.addons || [];
  const setAddons = (a) => onChange({ ...item, addons: a });
  return (
    <details className="mt-2 ml-0 sm:ml-14 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid={`item-options-${ci}-${ii}`}>
      <summary className="cursor-pointer text-white/60 text-xs font-semibold flex items-center gap-1.5 select-none">
        <Settings2 className="w-3.5 h-3.5" /> Opcijas & papildinājumi {(groups.length + addons.length) > 0 ? `(${groups.length + addons.length})` : ''}
      </summary>
      <div className="mt-3 space-y-3">
        {packagingPerItem && (
          <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid={`item-packaging-${ci}-${ii}`}>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-semibold">Pievienot iepakojuma maksu</p>
              <p className="text-white/40 text-[11px]">Izslēdz, piem., dzērieniem, kam nav vajadzīgs iepakojums.</p>
            </div>
            <Switch checked={item.packaging !== false} onCheckedChange={(v) => onChange({ ...item, packaging: v })} data-testid={`item-packaging-switch-${ci}-${ii}`} />
          </div>
        )}
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-lg border border-white/10 p-2.5" data-testid={`option-group-${ci}-${ii}-${gi}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input value={g.name} onChange={(e) => { const a = [...groups]; a[gi] = { ...g, name: e.target.value }; setGroups(a); }}
                placeholder="Grupas nosaukums (piem. Porcija, Asums)" className="bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`option-group-name-${ci}-${ii}-${gi}`} />
              <DelBtn onClick={() => setGroups(groups.filter((_, j) => j !== gi))} />
            </div>
            <div className="space-y-1.5">
              {(g.choices || []).map((c, cj) => (
                <div key={cj} className="flex items-center gap-2">
                  <Input value={c.label} onChange={(e) => { const a = [...groups]; const ch = [...(g.choices || [])]; ch[cj] = { ...c, label: e.target.value }; a[gi] = { ...g, choices: ch }; setGroups(a); }}
                    placeholder="Izvēle (piem. Liela)" className="flex-1 bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`option-choice-label-${ci}-${ii}-${gi}-${cj}`} />
                  <PriceInput step="0.01" value={c.price_delta} emptyValue={0} decimals={2} onChange={(v) => { const a = [...groups]; const ch = [...(g.choices || [])]; ch[cj] = { ...c, price_delta: v }; a[gi] = { ...g, choices: ch }; setGroups(a); }}
                    placeholder="+/- €" className="w-20 bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`option-choice-price-${ci}-${ii}-${gi}-${cj}`} />
                  <DelBtn onClick={() => { const a = [...groups]; a[gi] = { ...g, choices: (g.choices || []).filter((_, k) => k !== cj) }; setGroups(a); }} />
                </div>
              ))}
              <button type="button" onClick={() => { const a = [...groups]; a[gi] = { ...g, choices: [...(g.choices || []), { label: '', price_delta: 0 }] }; setGroups(a); }}
                className="text-teal-300 text-xs font-semibold inline-flex items-center gap-1" data-testid={`add-choice-${ci}-${ii}-${gi}`}><Plus className="w-3 h-3" /> Pievienot izvēli</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setGroups([...groups, { name: '', choices: [{ label: '', price_delta: 0 }] }])}
          className="text-teal-300 text-xs font-semibold inline-flex items-center gap-1" data-testid={`add-option-group-${ci}-${ii}`}><Plus className="w-3 h-3" /> Pievienot opciju grupu</button>
        <p className="text-white/35 text-[11px] leading-relaxed">Piem. grupa “Porcija” ar izvēlēm Maza (−1.00), Vidēja (0), Liela (+2.00). Cenas izmaiņa pieskaitās ēdiena bāzes cenai. “Asums”: Ass (0), Neass (0).</p>

        <div className="border-t border-white/10 pt-3 mt-1 space-y-1.5">
          <p className="text-white/70 text-xs font-semibold">Papildinājumi (klients pievieno ar vienu klikšķi)</p>
          {addons.map((a, ai) => (
            <div key={ai} className="flex items-center gap-2" data-testid={`addon-row-${ci}-${ii}-${ai}`}>
              <Input value={a.label} onChange={(e) => { const x = [...addons]; x[ai] = { ...a, label: e.target.value }; setAddons(x); }}
                placeholder="Papildinājums (piem. Dzēriens)" className="flex-1 bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`addon-label-${ci}-${ii}-${ai}`} />
              <PriceInput step="0.01" value={a.price} emptyValue={0} decimals={2} onChange={(v) => { const x = [...addons]; x[ai] = { ...a, price: v }; setAddons(x); }}
                placeholder="€" className="w-20 bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`addon-price-${ci}-${ii}-${ai}`} />
              <DelBtn onClick={() => setAddons(addons.filter((_, k) => k !== ai))} />
            </div>
          ))}
          <button type="button" onClick={() => setAddons([...addons, { label: '', price: 0 }])}
            className="text-teal-300 text-xs font-semibold inline-flex items-center gap-1" data-testid={`add-addon-${ci}-${ii}`}><Plus className="w-3 h-3" /> Pievienot papildinājumu</button>
        </div>
      </div>
    </details>
  );
}

// Online ordering settings: daily-special text + its price/options, the
// "allow menu-item ordering" toggle, and the shared delivery / pickup / packaging
// / payment settings. `showDailyText` controls whether the daily-special TEXT
// field is rendered here (the Menu template keeps it in Pamatinformācija).
export function OnlineOrderingSettings({ mc, setMc, showDailyText = false }) {
  const pkgFee = Number(mc.packaging_fee) || 0;
  const pkgPerItem = pkgFee > 0 && (mc.packaging_fee_mode || 'per_order') === 'per_item';
  return (
    <Section title="Pasūtīšana tiešsaistē">
      {showDailyText && (
        <Field label="Dienas piedāvājums">
          <Input value={mc.daily_special || ''} onChange={(e) => setMc({ daily_special: e.target.value })}
            placeholder="Piem. Zupa + otrais + dzēriens 6.90€" className="bg-white/5 border-white/10 text-white" data-testid="menu-daily-special" />
        </Field>
      )}
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="ds-orderable-row">
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold">Dienas piedāvājums pieejams pasūtīšanai</p>
          <p className="text-white/40 text-xs">Parādi “Pasūtīt” pogu pie dienas piedāvājuma (KoLV 10% komisija).</p>
        </div>
        <Switch checked={!!mc.daily_special_orderable} onCheckedChange={(v) => setMc({ daily_special_orderable: v })} data-testid="ds-orderable-toggle" />
      </div>
      {mc.daily_special_orderable && (
        <div className="mt-3 space-y-2">
          <Field label="Dienas piedāvājuma cena (€)">
            <PriceInput min="0" step="0.01" value={mc.daily_special_price} emptyValue={null} decimals={2}
              onChange={(v) => setMc({ daily_special_price: v })}
              placeholder="0.00" className="bg-white/5 border-white/10 text-white" data-testid="ds-price-input" />
          </Field>
          <p className="text-white/40 text-xs">Pievieno izvēles (piem. izmērs, piedeva) un papildinājumus (piem. dzēriens) — gluži kā ēdienkartes ēdieniem. Klients tos izvēlas, pievienojot dienas piedāvājumu grozam.</p>
          <ItemOptionsEditor
            ci="ds" ii="0"
            item={{ option_groups: mc.daily_special_option_groups || [], addons: mc.daily_special_addons || [], packaging: mc.daily_special_packaging !== false }}
            onChange={(nit) => setMc({ daily_special_option_groups: nit.option_groups || [], daily_special_addons: nit.addons || [], daily_special_packaging: nit.packaging !== false })}
            packagingPerItem={pkgPerItem}
          />
        </div>
      )}

      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mt-3" data-testid="menu-ordering-row">
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold">Ļaut pasūtīt ēdienkartes ēdienus</p>
          <p className="text-white/40 text-xs">Katram ēdienam ar cenu parādās “+” poga un grozs. Opcijas (izmērs, asums…) pievieno pie katra ēdiena sadaļā “Ēdienkarte”.</p>
        </div>
        <Switch checked={!!mc.menu_ordering_enabled} onCheckedChange={(v) => setMc({ menu_ordering_enabled: v })} data-testid="menu-ordering-toggle" />
      </div>

      {(mc.daily_special_orderable || mc.menu_ordering_enabled) && (
        <div className="mt-4 space-y-3">
          <p className="text-white/50 text-xs">Šie piegādes un maksājuma iestatījumi attiecas gan uz dienas piedāvājumu, gan ēdienu pasūtījumiem.</p>
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="ds-pickup-row">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold">Saņemt uz vietas</p>
              <p className="text-white/40 text-xs">Klients paņem pasūtījumu uz vietas.</p>
            </div>
            <Switch checked={mc.daily_special_allow_pickup !== false} onCheckedChange={(v) => setMc({ daily_special_allow_pickup: v })} data-testid="ds-pickup-switch" />
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="ds-delivery-row">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold">Piegāde</p>
              <p className="text-white/40 text-xs">Piegāde uz klienta norādīto adresi.</p>
            </div>
            <Switch checked={!!mc.daily_special_allow_delivery} onCheckedChange={(v) => setMc({ daily_special_allow_delivery: v })} data-testid="ds-delivery-switch" />
          </div>
          {mc.daily_special_allow_delivery && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Min. summa piegādei (€)">
                <PriceInput min="0" step="0.01" value={mc.daily_special_min_delivery} emptyValue={0} decimals={2}
                  onChange={(v) => setMc({ daily_special_min_delivery: v })}
                  placeholder="0.00" className="bg-white/5 border-white/10 text-white" data-testid="ds-min-delivery-input" />
              </Field>
              <Field label="Piegādes maksa (€)">
                <PriceInput min="0" step="0.01" value={mc.daily_special_delivery_fee} emptyValue={0} decimals={2}
                  onChange={(v) => setMc({ daily_special_delivery_fee: v })}
                  placeholder="0.00" className="bg-white/5 border-white/10 text-white" data-testid="ds-delivery-fee-input" />
              </Field>
              <Field label="Bezmaksas piegāde no (€)">
                <PriceInput min="0" step="0.01" value={mc.daily_special_free_delivery_from} emptyValue={0} decimals={2}
                  onChange={(v) => setMc({ daily_special_free_delivery_from: v })}
                  placeholder="0 = izslēgts" className="bg-white/5 border-white/10 text-white" data-testid="ds-free-delivery-input" />
              </Field>
              <Field label="Piegādes rādiuss (km)">
                <PriceInput min="0" step="0.5" value={mc.daily_special_delivery_radius_km} emptyValue={0}
                  onChange={(v) => setMc({ daily_special_delivery_radius_km: v })}
                  placeholder="0 = nerādīt" className="bg-white/5 border-white/10 text-white" data-testid="ds-delivery-radius-input" />
              </Field>
              <p className="col-span-full text-white/40 text-xs">Ja pasūtījuma summa sasniedz “Bezmaksas piegāde no”, piegādes maksa kļūst €0. Atstāj 0, lai izslēgtu. Rādiuss ir informatīvs — ja klients ir ārpus, viņš var sarunāt piegādi čatā.</p>
            </div>
          )}
          {mc.daily_special_allow_delivery && (
            <DeliveryHoursEditor value={mc.delivery_hours} onChange={(v) => setMc({ delivery_hours: v })} />
          )}
          {mc.daily_special_allow_pickup !== false && (
            <DeliveryHoursEditor value={mc.pickup_hours} onChange={(v) => setMc({ pickup_hours: v })}
              tid="pickup" title="Pašizvešanas laiks"
              toggleLabel="Ierobežot saņemšanas laiku"
              toggleDesc="Saņemšanas laiks tiek piedāvāts tikai darba laikā. Ja slēgts, klients joprojām var pasūtīt — saņemšana tiek piedāvāta pēc atvēršanas." />
          )}
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="ds-allow-online-row">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold">Maksāt online (karte caur KoLV)</p>
              <p className="text-white/40 text-xs">Tu saņem 90% maciņā.</p>
            </div>
            <Switch checked={mc.allow_online !== false} onCheckedChange={(v) => setMc({ allow_online: v })} data-testid="ds-allow-online-switch" />
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="ds-allow-onsite-row">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold">Maksāt saņemot (klātienē)</p>
              <p className="text-white/40 text-xs">Vēlāk atzīmē kā samaksātu — 10% kļūst par parādu.</p>
            </div>
            <Switch checked={!!mc.allow_onsite} onCheckedChange={(v) => setMc({ allow_onsite: v })} data-testid="ds-allow-onsite-switch" />
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-3" data-testid="packaging-fee-block">
            <div>
              <p className="text-white text-sm font-semibold">Iepakojuma maksa</p>
              <p className="text-white/40 text-xs">Automātiski pieskaitās pasūtījumam līdzņemšanai/piegādei. Atstāj 0, lai izslēgtu. (KoLV 10% komisija attiecas arī uz šo maksu.)</p>
            </div>
            <Field label="Iepakojuma maksa (€)">
              <PriceInput min="0" step="0.01" value={mc.packaging_fee} emptyValue={0} decimals={2}
                onChange={(v) => setMc({ packaging_fee: v })}
                placeholder="0.00" className="bg-white/5 border-white/10 text-white" data-testid="packaging-fee-input" />
            </Field>
            {pkgFee > 0 && (
              <>
                <div>
                  <p className="text-white/70 text-xs font-semibold mb-1.5">Kā rēķināt?</p>
                  <div className="grid grid-cols-2 gap-2" data-testid="packaging-mode">
                    <button type="button" onClick={() => setMc({ packaging_fee_mode: 'per_order' })}
                      className={`min-h-[42px] rounded-xl text-xs font-semibold border px-2 transition-colors ${(mc.packaging_fee_mode || 'per_order') === 'per_order' ? 'bg-teal-500/20 border-teal-400 text-white' : 'border-white/10 text-white/60 hover:border-white/25'}`}
                      data-testid="packaging-mode-per-order">Fiksēta par pasūtījumu</button>
                    <button type="button" onClick={() => setMc({ packaging_fee_mode: 'per_item' })}
                      className={`min-h-[42px] rounded-xl text-xs font-semibold border px-2 transition-colors ${mc.packaging_fee_mode === 'per_item' ? 'bg-teal-500/20 border-teal-400 text-white' : 'border-white/10 text-white/60 hover:border-white/25'}`}
                      data-testid="packaging-mode-per-item">Par katru ēdienu</button>
                  </div>
                  {mc.packaging_fee_mode === 'per_item' && (
                    <p className="text-white/40 text-[11px] mt-1.5">Maksa reizinās ar ēdienu skaitu grozā. Katram ēdienam vari to ieslēgt/izslēgt sadaļā “Ēdienkarte” → ēdiena “Opcijas & papildinājumi”.</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2.5" data-testid="packaging-pickup-row">
                    <div className="min-w-0 flex-1"><p className="text-white text-xs font-semibold">Saņemot uz vietas</p></div>
                    <Switch checked={mc.packaging_fee_pickup !== false} onCheckedChange={(v) => setMc({ packaging_fee_pickup: v })} data-testid="packaging-pickup-switch" />
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2.5" data-testid="packaging-delivery-row">
                    <div className="min-w-0 flex-1"><p className="text-white text-xs font-semibold">Piegādei</p></div>
                    <Switch checked={mc.packaging_fee_delivery !== false} onCheckedChange={(v) => setMc({ packaging_fee_delivery: v })} data-testid="packaging-delivery-switch" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

// The "Ēdienkarte" categories & items editor.
export function MenuCategoriesEditor({ mc, setMc, upload, uploading }) {
  const pkgFee = Number(mc.packaging_fee) || 0;
  const pkgPerItem = pkgFee > 0 && (mc.packaging_fee_mode || 'per_order') === 'per_item';
  return (
    <Section title="Ēdienkarte" action={
      <AddBtn label="Kategorija" onClick={() => setMc({ categories: [...(mc.categories || []), { id: `new-${Date.now()}`, name: 'Jauna kategorija', items: [] }] })} testid="add-category" />
    }>
      {(mc.categories || []).length === 0 && <p className="text-white/40 text-sm">Pievieno vismaz vienu kategoriju (piem. Uzkodas, Pamatēdieni, Dzērieni).</p>}
      <div className="space-y-6">
        {(mc.categories || []).map((cat, ci) => (
          <div key={cat.id || ci} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`category-block-${ci}`}>
            <div className="flex items-center gap-2 mb-3">
              <Input value={cat.name} onChange={(e) => { const a = [...mc.categories]; a[ci] = { ...cat, name: e.target.value }; setMc({ categories: a }); }}
                placeholder="Kategorijas nosaukums" className="bg-white/5 border-white/10 text-white font-semibold" data-testid={`category-name-${ci}`} />
              <DelBtn onClick={() => setMc({ categories: mc.categories.filter((_, j) => j !== ci) })} />
            </div>
            <div className="space-y-2">
              {cat.items?.map((it, ii) => {
                const patchItem = (nit) => { const a = [...mc.categories]; const items = [...cat.items]; items[ii] = nit; a[ci] = { ...cat, items }; setMc({ categories: a }); };
                return (
                <div key={ii} data-testid={`menu-item-wrap-${ci}-${ii}`}>
                  <div className="flex items-center gap-2" data-testid={`menu-item-row-${ci}-${ii}`}>
                    <ItemImagesField images={itemImages(it)} uploading={uploading}
                      onAddFile={(f) => upload(f, (u) => patchItem(withImages(it, [...itemImages(it), u])))}
                      onRemoveAt={(ri) => patchItem(withImages(it, itemImages(it).filter((_, k) => k !== ri)))}
                      testid={`menu-item-image-${ci}-${ii}`} />
                    <Input value={it.name} onChange={(e) => patchItem({ ...it, name: e.target.value })}
                      placeholder="Ēdiens" className="flex-1 min-w-0 bg-white/5 border-white/10 text-white" />
                    <Input value={it.description || ''} onChange={(e) => patchItem({ ...it, description: e.target.value })}
                      placeholder="Apraksts" className="flex-1 min-w-0 bg-white/5 border-white/10 text-white" />
                    <PriceInput value={it.price} emptyValue={null} decimals={2} onChange={(v) => patchItem({ ...it, price: v })}
                      placeholder="€" className="w-20 shrink-0 bg-white/5 border-white/10 text-white" data-testid={`menu-item-price-${ci}-${ii}`} />
                    <DelBtn onClick={() => { const a = [...mc.categories]; a[ci] = { ...cat, items: cat.items.filter((_, j) => j !== ii) }; setMc({ categories: a }); }} />
                  </div>
                  <ItemOptionsEditor item={it} ci={ci} ii={ii} onChange={patchItem} packagingPerItem={pkgPerItem} />
                </div>
                );
              })}
            </div>
            <button onClick={() => { const a = [...mc.categories]; a[ci] = { ...cat, items: [...(cat.items || []), { name: '', price: null, description: '', tags: [] }] }; setMc({ categories: a }); }}
              className="mt-3 inline-flex items-center gap-1 text-teal-300 text-xs font-semibold" data-testid={`add-item-${ci}`}>
              <Plus className="w-3.5 h-3.5" /> Pievienot ēdienu
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

// The "Galdiņu rezervācija" settings (deposit + payment options).
export function TableReservationEditor({ mc, setMc }) {
  return (
    <Section title="Galdiņu rezervācija">
      <div className="flex items-center justify-between">
        <p className="text-white/60 text-sm">Ļaut apmeklētājiem rezervēt galdiņu tiešsaistē</p>
        <Switch checked={!!mc.reservations_enabled} onCheckedChange={(v) => setMc({ reservations_enabled: v })} data-testid="menu-reservations-toggle" />
      </div>
      {mc.reservations_enabled && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-white/60 text-xs">Galdiņa depozīts (€) — atstāj 0 bezmaksas rezervācijai</label>
            <PriceInput min="0" step="0.01" value={mc.reservation_deposit} emptyValue={0} decimals={2}
              onChange={(v) => setMc({ reservation_deposit: v })}
              className="mt-1" placeholder="0.00" data-testid="menu-deposit-input" />
          </div>
          {Number(mc.reservation_deposit) > 0 && (
            <>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="menu-allow-online-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Depozītu maksāt online (karte caur KoLV)</p>
                  <p className="text-white/40 text-xs">Tu saņem 90% maciņā.</p>
                </div>
                <Switch checked={mc.allow_online !== false} onCheckedChange={(v) => setMc({ allow_online: v })} data-testid="menu-allow-online-switch" />
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="menu-allow-onsite-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Depozītu maksāt klātienē / attālināti</p>
                  <p className="text-white/40 text-xs">Vēlāk atzīmē kā samaksātu — 10% kļūst par parādu.</p>
                </div>
                <Switch checked={!!mc.allow_onsite} onCheckedChange={(v) => setMc({ allow_onsite: v })} data-testid="menu-allow-onsite-switch" />
              </div>
            </>
          )}
          <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3 flex items-start gap-3" data-testid="menu-payments-info-card">
            <CreditCard className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-white/50 text-xs leading-relaxed">Ja depozīts <b className="text-white/70">&gt; €0</b>, klients izvēlas kā maksāt no tevis iespējotajām opcijām (KoLV 10% komisija). Depozīts <b className="text-white/70">€0 = bezmaksas</b> galdiņa pieprasījums.</p>
          </div>
        </div>
      )}
    </Section>
  );
}
